/* import __COLOCATED_TEMPLATE__ from './plan-card-container.hbs'; */
/* RESPONSIBLE TEAM: team-self-serve */
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { dropTask } from 'ember-concurrency-decorators';
import { taskFor } from 'ember-concurrency-ts';
import {
  AI_INSIGHTS_BASE_ID,
  FIN_AI_COPILOT_BASE_ID,
  FIN_PRICE_PER_RESOLUTION,
  PLAN_DATA,
  PRICING_5_1_PRICING_MODEL,
  PRICING_5_EARLY_STAGE_BUNDLE,
  PRICING_5_X_CORE_ADVANCED_ID,
  PRICING_5_X_CORE_ESSENTIAL_ID,
  PRICING_5_X_CORE_PLANS,
  PROACTIVE_SUPPORT_PLUS_BASE_ID,
} from 'embercom/lib/billing';
import { EARLY_STAGE_ADDON_DISPLAY_ORDER } from 'embercom/lib/billing/addon-feature-keys';
import { Metric } from 'embercom/models/data/pricing/metric-types';
import type IntlService from 'embercom/services/intl';
import type EssentialDowngradeWarningModalService from 'embercom/services/essential-downgrade-warning-modal-service';

interface Args {
  earlyStageGraduation: any;
  customer: any;
}

interface Signature {
  Element: HTMLDivElement;
  Args: Args;
}

const MIN_EARLY_STAGE_SEATS = 6;

export default class PlanCardContainer extends Component<Signature> {
  @service intl!: IntlService;
  @service customerService: any;
  @service purchaseAnalyticsService: any;
  @service notificationsService: any;
  @service appService: any;
  @service router: any;
  @service declare essentialDowngradeWarningModalService: EssentialDowngradeWarningModalService;

  @tracked showStayOnEarlyStageModal = false;
  @tracked showFeatureComparisonModal = false;
  @tracked showEssentialDowngradeWarningModal = false;

  constructor(owner: unknown, args: Args) {
    super(owner, args);
    this.essentialDowngradeWarningModalService.loadEvaluationCriteria();
  }

  get earlyStageGraduation() {
    return this.args.earlyStageGraduation;
  }

  get timeExtension() {
    return this.customerService.customer.graduatingWithLowUsageDiscount;
  }

  get currentStepYearNumber() {
    return this.earlyStageGraduation.currentStepYearNumber;
  }

  get nextStepYearNumber() {
    return this.earlyStageGraduation.nextStepYearNumber;
  }

  get isYear3Customer() {
    return this.currentStepYearNumber === 3;
  }

  get currentDiscountPercentage() {
    return Math.floor(this.earlyStageGraduation.currentStepPercentageDiscount);
  }

  get nextYearDiscountPercentage() {
    return Math.floor(this.earlyStageGraduation.nextStepPercentageDiscount);
  }

  get shouldDisableEarlyStagePlan() {
    return (
      this.timeExtension ||
      (this.earlyStageGraduation.progressionConfirmedByCustomer &&
        this.earlyStageGraduation.isGraduatingToEarlyStage)
    );
  }

  get shouldDisableRecommendedPlan() {
    return (
      this.earlyStageGraduation.progressionConfirmedByCustomer &&
      !this.earlyStageGraduation.isGraduatingToEarlyStage
    );
  }

  get earlyStagePlansPrice() {
    return this.customerService.getPriceFromPlanIds(
      PRICING_5_EARLY_STAGE_BUNDLE.map(Number),
      this.earlyStageGraduation.pricingModelForEarlyStagePlan,
    );
  }

  get defaultPricingModelPrice() {
    return this.customerService.getPriceFromPlanIds(
      PRICING_5_EARLY_STAGE_BUNDLE.map(Number),
      PRICING_5_1_PRICING_MODEL,
    );
  }

  get earlyStageDiscountedPrice() {
    let total =
      this.earlyStageAdvancedDiscountedPrice +
      this.proactiveSupportDiscountedPrice +
      this.copilotDiscountedPrice;

    if (!this.isYear3Customer) {
      total += this.finAiAgentDiscountedPrice;
    }

    if (this.canSeeAiInsightsAddon) {
      total += this.aiInsightsDiscountedPrice;
    }

    return total;
  }

  get earlyStageAdvancedDiscountedPrice(): number {
    let priceBreakdown = this.earlyStagePriceBreakdown(PRICING_5_X_CORE_ADVANCED_ID);
    let price = priceBreakdown.charges.find(
      (charge: any) => charge.pricing_metric === Metric.core_seat_count,
    ).price;

    return price / 100;
  }

  get proactiveSupportDiscountedPrice() {
    return this.convertedPriceForPlan(PROACTIVE_SUPPORT_PLUS_BASE_ID);
  }

  get copilotDiscountedPrice() {
    return this.isYear3Customer ? 0 : this.convertedPriceForPlan(FIN_AI_COPILOT_BASE_ID);
  }

  get aiInsightsDiscountedPrice() {
    return this.convertedPriceForPlan(AI_INSIGHTS_BASE_ID);
  }

  get numberOfResolutionsAllowed() {
    let priceBreakdown = this.earlyStagePriceBreakdown(PRICING_5_X_CORE_ADVANCED_ID);
    let charge = priceBreakdown.charges.find(
      (charge: any) => charge.pricing_metric === Metric.resolutions_with_custom_answers,
    );
    return charge?.plan_limit?.minimum || 0;
  }

  get pricePerResolution() {
    return FIN_PRICE_PER_RESOLUTION;
  }

  get finAiAgentDiscountedPrice() {
    return 0;
  }

  get earlyStageFullPrice() {
    let total =
      this.earlyStageAdvancedFullPrice + this.proactiveSupportFullPrice + this.copilotFullPrice;

    if (!this.isYear3Customer) {
      total += this.finAiAgentFullPrice;
    }

    if (this.canSeeAiInsightsAddon) {
      total += this.aiInsightsFullPrice;
    }

    return total;
  }

  get earlyStageAdvancedFullPrice() {
    return this.defaultAdvancedCorePrice() * this.earlyStageSeatCount;
  }

  get proactiveSupportFullPrice() {
    return this.defaultPriceForPlan(PROACTIVE_SUPPORT_PLUS_BASE_ID);
  }

  get copilotFullPrice() {
    return this.isYear3Customer
      ? this.year3CopilotFullPrice
      : this.defaultCopilotPrice() * this.earlyStageSeatCount;
  }

  get year3CopilotFullPrice() {
    return this.defaultCopilotPrice() * this.copilotSeatCount;
  }

  get aiInsightsFullPrice() {
    return this.defaultPriceForPlan(AI_INSIGHTS_BASE_ID);
  }

  get finAiAgentFullPrice() {
    return this.numberOfResolutionsAllowed * FIN_PRICE_PER_RESOLUTION;
  }

  get recommendedCorePlan() {
    let planIds = this.earlyStageGraduation.recommendedPlanIds;
    if (this.shouldDisableRecommendedPlan) {
      planIds = this.earlyStageGraduation.planIds;
    }

    return planIds.find((id: number) => PRICING_5_X_CORE_PLANS.includes(id.toString()));
  }

  get isEssentialPlan() {
    return this.recommendedCorePlan.toString() === PRICING_5_X_CORE_ESSENTIAL_ID;
  }

  get shouldShowEssentialWarning() {
    return this.isEssentialPlan && this.essentialDowngradeWarningModalService.shouldShowWarning;
  }

  get recommendedCorePlanData() {
    return PLAN_DATA[this.recommendedCorePlan];
  }

  get recommendedCorePlanName() {
    return this.recommendedCorePlanData.name;
  }

  get recommendedPricePerSeat() {
    return this.recommendedCorePlanData.pricePerSeat ?? 0;
  }

  get recommendedSeatPrice() {
    return this.recommendedPricePerSeat * this.coreSeatCount;
  }

  get recommendedLightSeatCount() {
    return this.recommendedCorePlanData.liteSeatLimit;
  }

  get coreSeatCount() {
    return (
      this.appService.app.humanAdminsWithCoreSeat.length || this.appService.app.humanAdmins.length
    );
  }

  get earlyStageSeatCount() {
    if (this.isYear3Customer) {
      return this.coreSeatCount;
    }
    return Math.max(MIN_EARLY_STAGE_SEATS, this.coreSeatCount);
  }

  get hasCopilotSeats() {
    return this.appService.app.humanAdminsWithCopilotSeat.length > 0;
  }

  get canSeeAiInsightsAddon() {
    return this.appService.app.canSeeAiInsightsAddon;
  }

  get copilotSeatCount() {
    return this.appService.app.humanAdminsWithCopilotSeat.length;
  }

  earlyStagePriceBreakdown(planId: string) {
    return this.earlyStagePlansPrice.breakdown.find(
      (breakdown: any) => breakdown.plan_id.toString() === planId,
    );
  }

  defaultPriceBreakdown(planId: string) {
    return this.defaultPricingModelPrice.breakdown.find(
      (breakdown: any) => breakdown.plan_id.toString() === planId,
    );
  }

  convertedPriceForPlan(planId: string): number {
    let amount = this.earlyStagePriceBreakdown(planId).plan_starting_price;
    return amount / 100;
  }

  defaultPriceForPlan(planId: string): number {
    let amount = this.defaultPriceBreakdown(planId).plan_starting_price;
    return amount / 100;
  }

  defaultAdvancedCorePrice(): number {
    let priceBreakdown = this.defaultPriceBreakdown(PRICING_5_X_CORE_ADVANCED_ID);
    let perUnitPrice = priceBreakdown.charges.find(
      (charge: any) => charge.pricing_metric === Metric.core_seat_count,
    ).per_unit_price;

    return perUnitPrice / 100;
  }

  defaultCopilotPrice(): number {
    let priceBreakdown = this.defaultPriceBreakdown(FIN_AI_COPILOT_BASE_ID);
    let perUnitPrice = priceBreakdown.charges.find(
      (charge: any) => charge.pricing_metric === Metric.copilot_seat_count,
    ).per_unit_price;

    return perUnitPrice / 100;
  }

  get recommendedPlanIds() {
    return this.shouldDisableRecommendedPlan
      ? this.earlyStageGraduation.planIds
      : this.earlyStageGraduation.recommendedPlanIds;
  }

  get recommendedCopilotPrice() {
    return this.defaultCopilotPrice() * this.copilotSeatCount;
  }

  get recommendedPlanTotalPrice() {
    let total = this.recommendedSeatPrice;

    if (this.shouldDisableRecommendedPlan) {
      if (this.recommendedPlanIds?.includes(parseInt(PROACTIVE_SUPPORT_PLUS_BASE_ID, 10))) {
        total += this.proactiveSupportFullPrice;
      }
      if (this.recommendedPlanIds?.includes(parseInt(FIN_AI_COPILOT_BASE_ID, 10))) {
        total += this.recommendedCopilotPrice;
      }
      if (
        this.canSeeAiInsightsAddon &&
        this.recommendedPlanIds?.includes(parseInt(AI_INSIGHTS_BASE_ID, 10))
      ) {
        total += this.aiInsightsFullPrice;
      }
    }

    return total;
  }

  get selectedAddonsData() {
    if (!this.shouldDisableRecommendedPlan) {
      return [];
    }

    let addons = [];

    for (let addonPlanId of EARLY_STAGE_ADDON_DISPLAY_ORDER) {
      let shouldInclude =
        this.recommendedPlanIds.includes(parseInt(addonPlanId, 10)) &&
        (addonPlanId !== AI_INSIGHTS_BASE_ID || this.canSeeAiInsightsAddon);

      if (shouldInclude) {
        if (addonPlanId === FIN_AI_COPILOT_BASE_ID) {
          if (this.copilotSeatCount > 0) {
            addons.push({
              planId: parseInt(addonPlanId, 10),
              nameKey:
                'new-settings.workspace.billing.update-subscription.plan-card.fin-ai-copilot',
              price: this.recommendedCopilotPrice,
              seatCount: this.copilotSeatCount,
            });
          }
        } else if (addonPlanId === AI_INSIGHTS_BASE_ID) {
          addons.push({
            planId: parseInt(addonPlanId, 10),
            nameKey: 'new-settings.workspace.billing.update-subscription.plan-card.ai-insights',
            price: this.aiInsightsFullPrice,
          });
        } else if (addonPlanId === PROACTIVE_SUPPORT_PLUS_BASE_ID) {
          addons.push({
            planId: parseInt(addonPlanId, 10),
            nameKey:
              'new-settings.workspace.billing.update-subscription.plan-card.proactive-support-plus',
            price: this.proactiveSupportFullPrice,
          });
        }
      }
    }

    return addons;
  }

  @action
  closeEarlyStageModal() {
    this.showStayOnEarlyStageModal = false;
  }

  @action
  openStayOnEarlyStageModal() {
    this.purchaseAnalyticsService.trackEvent({
      action: 'clicked',
      context: 'early_stage_plan_card',
      place: 'update_subscription',
      object: 'stay_on_early_stage',
    });
    this.showStayOnEarlyStageModal = true;
  }

  @action
  redirectToPlanBuilder() {
    this.purchaseAnalyticsService.trackEvent({
      action: 'clicked',
      context: 'recommended_plan_card',
      place: 'update_subscription',
      object: 'configure_your_new_subscription',
      plan_name: this.recommendedCorePlanName,
    });

    if (this.shouldShowEssentialWarning) {
      this.showEssentialDowngradeWarningModal = true;
      return;
    }

    this.performRedirectToPlanBuilder();
  }

  @action
  performRedirectToPlanBuilder() {
    this.router.transitionTo('apps.app.teams-checkout', this.customerService.app.id, {
      queryParams: {
        plan_id: this.recommendedCorePlan,
      },
    });
  }

  @action
  closeEssentialDowngradeWarningModal() {
    this.showEssentialDowngradeWarningModal = false;
  }

  @action
  onContinueEssentialDowngrade() {
    this.closeEssentialDowngradeWarningModal();
    this.performRedirectToPlanBuilder();
  }

  get tooltipText() {
    if (this.timeExtension) {
      return this.intl.t(
        'new-settings.workspace.billing.update-subscription.plan-card.disabled-button-tooltip-with-es-pre-selected',
      );
    }

    let formattedDate = this.intl.formatDate(this.earlyStageGraduation.graduationDate, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });

    if (this.isYear3Customer) {
      return this.intl.t(
        'new-settings.workspace.billing.update-subscription.plan-card.disabled-button-tooltip-year-3',
        {
          htmlSafe: true,
          startDate: formattedDate,
        },
      );
    }

    return this.intl.t(
      'new-settings.workspace.billing.update-subscription.plan-card.disabled-button-tooltip',
      {
        htmlSafe: true,
        startDate: formattedDate,
        isEarlyStageConfirmed: this.shouldDisableEarlyStagePlan,
      },
    );
  }

  @action
  async saveEarlyStagePlans() {
    let priceSetIdentifier = this.earlyStageGraduation.pricingModelForEarlyStagePlan;
    let planIds = this.earlyStageGraduation.earlyStagePlanIds;

    this.earlyStageGraduation.planIds = planIds;
    this.earlyStageGraduation.priceSetIdentifier = priceSetIdentifier;

    this.purchaseAnalyticsService.trackEvent({
      action: 'clicked',
      context: 'stay_on_early_stage_modal',
      place: 'update_subscription',
      object: 'stay_on_early_stage',
      plan_ids: planIds,
      price_set_identifier: priceSetIdentifier,
    });

    await taskFor(this.saveEarlyStageGraduation).perform();
    this.closeEarlyStageModal();
  }

  @dropTask
  *saveEarlyStageGraduation() {
    try {
      yield this.earlyStageGraduation.save();
      this.notificationsService.notifyConfirmation(
        this.intl.t('billing.summary.current-and-future-price-component.success-saving-plan'),
      );
      this.router.transitionTo('apps.app.billing.summary', this.customerService.app.id, {
        queryParams: {
          esChoiceConfirmation: true,
        },
      });
    } catch (err) {
      this.notificationsService.notifyError(
        this.intl.t('billing.summary.current-and-future-price-component.error-saving-plan'),
      );
    }
  }

  @action
  onFeatureComparisonModalClose() {
    this.showFeatureComparisonModal = false;
  }

  @action
  onReviewFeatures() {
    this.showFeatureComparisonModal = true;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Billing::UpdateEarlyStageSubscription::PlanCardContainer': typeof PlanCardContainer;
    'billing/update-early-stage-subscription/plan-card-container': typeof PlanCardContainer;
  }
}
